/**
 * Root Cause Detector
 * Proactively detects and prevents conditions that lead to perpetual loading
 */

import { ref } from 'vue';
import { useApplicationStore } from '@/store/application.store';
// NOTE: auth is imported dynamically to prevent Firebase initialization race conditions
// See FIREBASE_INITIALIZATION_FIX.md for details
import { validateSessionWithRefresh, isTokenExpiredClientSide, attemptTokenRefresh } from '@/store/helpers/authenticate/tokens';
import { useUserStore } from '@/store/user.store';

// Lazy auth getter to prevent early Firebase initialization
// Use @/firebase barrel which exports the resilient Auth proxy
let _authCache: Awaited<typeof import('@/firebase')>['auth'] | null = null;
async function getAuth() {
  if (!_authCache) {
    const { auth } = await import('@/firebase');
    _authCache = auth;
  }
  return _authCache;
}

export interface RootCauseIssue {
  id: string;
  category: 'auth' | 'session' | 'network' | 'loader' | 'reactivity' | 'router' | 'performance';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  detectedAt: number;
  preventionAction?: () => Promise<void>;
  metadata?: Record<string, any>;
}

export class RootCauseDetector {
  private static instance: RootCauseDetector;
  private issues = ref<RootCauseIssue[]>([]);
  private monitoringActive = false;
  private intervals: NodeJS.Timeout[] = [];

  private constructor() {}

  static getInstance(): RootCauseDetector {
    if (!RootCauseDetector.instance) {
      RootCauseDetector.instance = new RootCauseDetector();
    }
    return RootCauseDetector.instance;
  }

  /**
   * Start proactive monitoring for root causes
   */
  startMonitoring() {
    if (this.monitoringActive) return;
    
    console.log('🔍 [RootCauseDetector] Starting proactive monitoring');
    this.monitoringActive = true;
    
    // Monitor different categories
    this.monitorAuthentication();
    this.monitorSessionHealth(); // New comprehensive session monitoring
    this.monitorNetworkConnectivity();
    this.monitorDataLoaders();
    this.monitorVueReactivity();
    this.monitorRouterHealth();
    this.monitorPerformance();
  }

  /**
   * Stop monitoring and clean up
   */
  stopMonitoring() {
    this.monitoringActive = false;
    this.intervals.forEach(interval => clearInterval(interval));
    this.intervals = [];
    console.log('🔍 [RootCauseDetector] Monitoring stopped');
  }

  /**
   * Get current issues
   */
  getIssues(): RootCauseIssue[] {
    return this.issues.value;
  }

  /**
   * Monitor authentication-related issues
   */
  private monitorAuthentication() {
    const checkAuth = async () => {
      try {
        const auth = await getAuth();
        
        // Safely get current user - auth might not be fully initialized
        let currentUser = null;
        try {
          currentUser = auth.currentUser;
        } catch {
          // Auth not initialized yet - skip auth checks this cycle
          return;
        }
        
        // Check for token expiry
        if (currentUser) {
          currentUser.getIdToken(false).then(token => {
            if (!token) {
              this.reportIssue({
                id: 'auth-token-missing',
                category: 'auth',
                severity: 'high',
                description: 'Authentication token missing - likely expired during dormancy',
                detectedAt: Date.now(),
                preventionAction: async () => {
                  console.log('🔧 [RootCauseDetector] Refreshing expired auth token');
                  try {
                    const auth = await getAuth();
                    await auth.currentUser?.getIdToken(true);
                  } catch (e) {
                    console.warn('[RootCauseDetector] Failed to refresh token:', e);
                  }
                }
              });
            }
          }).catch(error => {
            this.reportIssue({
              id: 'auth-token-error',
              category: 'auth',
              severity: 'critical',
              description: `Authentication token error: ${error.message}`,
              detectedAt: Date.now(),
              preventionAction: async () => {
                console.log('🔧 [RootCauseDetector] Handling auth token error');
                // Force re-authentication
                if (typeof window !== 'undefined') {
                  window.location.href = '/login';
                }
              }
            });
          });
        }

        // Check for session storage issues
        const descopeSession = localStorage.getItem('descope_session');
        if (!descopeSession && currentUser) {
          this.reportIssue({
            id: 'session-storage-missing',
            category: 'auth',
            severity: 'medium',
            description: 'Descope session missing from localStorage but user authenticated',
            detectedAt: Date.now()
          });
        }

      } catch (error) {
        this.reportIssue({
          id: 'auth-check-failed',
          category: 'auth',
          severity: 'high',
          description: `Authentication check failed: ${error}`,
          detectedAt: Date.now()
        });
      }
    };

    const interval = setInterval(checkAuth, 30000); // Check every 30 seconds to reduce churn
    this.intervals.push(interval);
  }

  /**
   * Monitor comprehensive session health and staleness
   */
  private monitorSessionHealth() {
    let lastSessionValidation = 0;
    let consecutiveFailures = 0;
    const MAX_CONSECUTIVE_FAILURES = 3;
    const SESSION_CHECK_INTERVAL = 120000; // 2 minutes, throttle validation frequency
    
    const checkSessionHealth = async () => {
      try {
        const now = Date.now();
        const userStore = useUserStore();
        const auth = await getAuth();
        
        // Skip if not authenticated
        if (!userStore.isAuthenticated) {
          consecutiveFailures = 0;
          return;
        }

        // Check session token presence and basic validity
        const descopeToken = localStorage.getItem('descope_session');
        if (!descopeToken) {
          // Safely check Firebase user - auth might not be initialized yet
          let hasFirebaseUser = false;
          try {
            hasFirebaseUser = !!auth.currentUser;
          } catch {
            // Auth not initialized yet
          }
          
          this.reportIssue({
            id: 'session-token-missing',
            category: 'session',
            severity: 'critical',
            description: 'Session token is missing from localStorage while user appears authenticated',
            detectedAt: now,
            metadata: { 
              isAuthenticated: userStore.isAuthenticated,
              hasFirebaseUser 
            },
            preventionAction: async () => {
              console.log('🔧 [RootCauseDetector] Attempting to restore session');
              // Force logout and redirect to login
              if (typeof window !== 'undefined') {
                localStorage.clear();
                window.location.href = '/login';
              }
            }
          });
          return;
        }

        // Client-side token expiry check (confirm with validation before reporting)
        if (isTokenExpiredClientSide(descopeToken)) {
          const validation = await validateSessionWithRefresh();
          if (!validation.valid) {
            this.reportIssue({
              id: 'session-token-expired-client',
              category: 'session',
              severity: 'high',
              description: 'Session token appears expired based on client-side validation',
              detectedAt: now,
              metadata: { tokenLength: descopeToken.length, validationError: validation.error },
              preventionAction: async () => {
                console.log('🔧 [RootCauseDetector] Attempting to refresh expired token');
                const refreshResult = await attemptTokenRefresh();
                if (!refreshResult.success) {
                  // Force re-authentication
                  if (typeof window !== 'undefined') {
                    localStorage.clear();
                    window.location.href = '/login';
                  }
                }
              }
            });
          } else {
            // Clear any stale issue if validation succeeded
            this.clearIssue('session-token-expired-client');
          }
        }

        // Comprehensive session validation (throttled)
        if (now - lastSessionValidation > SESSION_CHECK_INTERVAL) {
          lastSessionValidation = now;
          
          try {
            const validationResult = await validateSessionWithRefresh();
            
            if (!validationResult.valid) {
              consecutiveFailures++;
              
              this.reportIssue({
                id: 'session-validation-failed',
                category: 'session',
                severity: consecutiveFailures >= MAX_CONSECUTIVE_FAILURES ? 'critical' : 'high',
                description: `Session validation failed (${consecutiveFailures}/${MAX_CONSECUTIVE_FAILURES} attempts): ${validationResult.error}`,
                detectedAt: now,
                metadata: {
                  consecutiveFailures,
                  lastError: validationResult.error,
                  tokenPresent: !!descopeToken
                },
                preventionAction: async () => {
                  console.log('🔧 [RootCauseDetector] Handling session validation failure');
                  if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
                    // Clear all auth data and redirect to login
                    localStorage.clear();
                    if (typeof window !== 'undefined') {
                      window.location.href = '/login';
                    }
                  } else {
                    // Attempt token refresh
                    await attemptTokenRefresh();
                  }
                }
              });
            } else {
              // Validation successful - reset failure counter and clear related issues
              consecutiveFailures = 0;
              this.clearIssue('session-validation-failed');
              this.clearIssue('session-token-expired-client');
              
              // Log if session was refreshed
              if (validationResult.refreshed) {
                console.log('🔄 [RootCauseDetector] Session was automatically refreshed');
              }
            }
            
          } catch (validationError) {
            consecutiveFailures++;
            this.reportIssue({
              id: 'session-validation-error',
              category: 'session',
              severity: 'high',
              description: `Session validation threw error: ${validationError}`,
              detectedAt: now,
              metadata: {
                consecutiveFailures,
                error: validationError instanceof Error ? validationError.message : String(validationError)
              }
            });
          }
        }

        // Cross-tab session sync check
        this.checkCrossTabSessionSync(now);
        
        // Detect stale browser sessions (dormant tabs)
        await this.detectStaleSession(now);
        
      } catch (error) {
        console.error('[RootCauseDetector] Session health check error:', error);
        this.reportIssue({
          id: 'session-health-check-error',
          category: 'session',
          severity: 'medium',
          description: `Session health monitoring failed: ${error}`,
          detectedAt: Date.now()
        });
      }
    };

    // Start session health monitoring
    const interval = setInterval(checkSessionHealth, 60000); // Check every 60 seconds
    this.intervals.push(interval);
  }

  /**
   * Check for cross-tab session synchronization issues
   */
  private checkCrossTabSessionSync(now: number) {
    try {
      // Check if session was updated in another tab
      const sessionUpdateKey = 'session_last_update';
      const lastUpdate = localStorage.getItem(sessionUpdateKey);
      
      if (!lastUpdate) {
        // Initialize tracking
        localStorage.setItem(sessionUpdateKey, now.toString());
        return;
      }

      const lastUpdateTime = parseInt(lastUpdate);
      const timeSinceUpdate = now - lastUpdateTime;
      
      // If another tab updated the session very recently but we didn't see the change
      if (timeSinceUpdate < 5000 && timeSinceUpdate > 0) {
        const currentToken = localStorage.getItem('descope_session');
        if (currentToken) {
          // Check if our token is different from what we expect
          const expectedToken = localStorage.getItem('descope_session_backup');
          if (expectedToken && expectedToken !== currentToken) {
            this.reportIssue({
              id: 'cross-tab-session-conflict',
              category: 'session',
              severity: 'medium',
              description: 'Session token changed in another tab - possible cross-tab authentication conflict',
              detectedAt: now,
              metadata: { timeSinceUpdate },
              preventionAction: async () => {
                console.log('🔧 [RootCauseDetector] Resolving cross-tab session conflict');
                // Validate current session to ensure it's valid
                await validateSessionWithRefresh();
              }
            });
          }
        }
      }
      
      // Update our tracking
      localStorage.setItem(sessionUpdateKey, now.toString());
      
    } catch (error) {
      console.error('[RootCauseDetector] Cross-tab sync check error:', error);
    }
  }

  /**
   * Detect stale browser sessions (tabs that have been dormant)
   */
  private async detectStaleSession(now: number) {
    try {
      const lastActivityKey = 'session_last_activity';
      const lastActivity = localStorage.getItem(lastActivityKey);
      
      if (!lastActivity) {
        localStorage.setItem(lastActivityKey, now.toString());
        return;
      }

      const lastActivityTime = parseInt(lastActivity);
      const timeSinceActivity = now - lastActivityTime;
      // Allow runtime config for stale threshold; default 20 minutes, cap 24h
      const configuredMs = Number((import.meta as any).env?.VITE_STALE_SESSION_THRESHOLD_MS ?? (20 * 60 * 1000));
      const STALE_SESSION_THRESHOLD = Number.isFinite(configuredMs) ? Math.min(Math.max(configuredMs, 5 * 60 * 1000), 24 * 60 * 60 * 1000) : 20 * 60 * 1000;
      
      if (timeSinceActivity > STALE_SESSION_THRESHOLD) {
        // Before reporting stale, ensure session validates (avoid false alarms)
        const validation = await validateSessionWithRefresh();
        if (!validation.valid) {
          this.reportIssue({
            id: 'stale-session-detected',
            category: 'session',
            severity: 'medium',
            description: `Browser session appears stale (${Math.round(timeSinceActivity / 60000)} minutes inactive)`,
            detectedAt: now,
            metadata: { 
              timeSinceActivity,
              lastActivityTime: new Date(lastActivityTime).toISOString(),
              validationError: validation.error
            },
            preventionAction: async () => {
              console.log('🔧 [RootCauseDetector] Refreshing stale session');
              // Validate and refresh session
              const result = await validateSessionWithRefresh();
              if (result.valid) {
                // Update activity timestamp
                localStorage.setItem(lastActivityKey, now.toString());
                this.clearIssue('stale-session-detected');
              }
            }
          });
        } else {
          // Session is valid; quietly update activity to current time and clear any prior issue
          localStorage.setItem(lastActivityKey, now.toString());
          this.clearIssue('stale-session-detected');
        }
      } else {
        // Update activity timestamp periodically
        if (timeSinceActivity > 60000) { // Update every minute
          localStorage.setItem(lastActivityKey, now.toString());
        }
      }
      
    } catch (error) {
      console.error('[RootCauseDetector] Stale session detection error:', error);
    }
  }

  /**
   * Monitor network connectivity issues
   */
  private monitorNetworkConnectivity() {
    // Online/offline detection
    const handleOnline = () => {
      console.log('🌐 [RootCauseDetector] Network restored');
      this.clearIssue('network-offline');
    };

    const handleOffline = () => {
      this.reportIssue({
        id: 'network-offline',
        category: 'network',
        severity: 'high',
        description: 'Network connectivity lost - may cause loading failures',
        detectedAt: Date.now(),
        preventionAction: async () => {
          console.log('🔧 [RootCauseDetector] Queuing retry for when network returns');
          // Wait for network to return then retry
          await new Promise(resolve => {
            const checkOnline = () => {
              if (navigator.onLine) {
                resolve(void 0);
              } else {
                setTimeout(checkOnline, 1000);
              }
            };
            checkOnline();
          });
        }
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check for slow network
    const checkNetworkSpeed = () => {
      const start = performance.now();
      fetch('/favicon.ico?' + Date.now(), { cache: 'no-cache' })
        .then(() => {
          const duration = performance.now() - start;
          if (duration > 5000) { // >5 seconds for favicon
            this.reportIssue({
              id: 'network-slow',
              category: 'network',
              severity: 'medium',
              description: `Slow network detected: ${Math.round(duration)}ms for simple request`,
              detectedAt: Date.now()
            });
          } else {
            this.clearIssue('network-slow');
          }
        })
        .catch(() => {
          this.reportIssue({
            id: 'network-unreachable',
            category: 'network',
            severity: 'critical',
            description: 'Network requests failing - server unreachable',
            detectedAt: Date.now()
          });
        });
    };

    const networkInterval = setInterval(checkNetworkSpeed, 10000); // Check every 10 seconds
    this.intervals.push(networkInterval);
  }

  /**
   * Monitor data loader issues
   */
  private monitorDataLoaders() {
    // Track loader execution times
    const originalConsoleLog = console.log;
    const loaderStartTimes = new Map<string, number>();

    // Intercept loader logs to detect issues
    console.log = (...args) => {
      const message = args.join(' ');
      
      // Detect loader starts
      if (message.includes('[DataLoader] Executing') && message.includes('loaders for route:')) {
        const routeName = args[args.length - 1];
        loaderStartTimes.set(routeName, Date.now());
      }
      
      // Detect loader completions
      if (message.includes('[DataLoader] All loaders completed successfully')) {
        const routeName = Array.from(loaderStartTimes.keys()).pop();
        if (routeName) {
          const duration = Date.now() - (loaderStartTimes.get(routeName) || 0);
          loaderStartTimes.delete(routeName);
          
          if (duration > 15000) { // >15 seconds
            this.reportIssue({
              id: `loader-slow-${routeName}`,
              category: 'loader',
              severity: 'medium',
              description: `Data loader slow for ${routeName}: ${duration}ms`,
              detectedAt: Date.now()
            });
          }
        }
      }
      
      // Detect loader failures
      if (message.includes('[DataLoader] Failed to execute loader')) {
        this.reportIssue({
          id: 'loader-execution-failed',
          category: 'loader',
          severity: 'high',
          description: 'Data loader execution failed',
          detectedAt: Date.now(),
          preventionAction: async () => {
            console.log('🔧 [RootCauseDetector] Clearing loader cache and retrying');
            // Clear caches and retry
            if ((window as any).useApplicationStore) {
              const appStore = (window as any).useApplicationStore();
              appStore.setState({ key: 'isPageReady', value: true });
            }
          }
        });
      }

      originalConsoleLog.apply(console, args);
    };

    // Check for stuck loaders
    const checkStuckLoaders = () => {
      loaderStartTimes.forEach((startTime, routeName) => {
        const duration = Date.now() - startTime;
        if (duration > 25000) { // >25 seconds (before 30s timeout)
          this.reportIssue({
            id: `loader-stuck-${routeName}`,
            category: 'loader',
            severity: 'critical',
            description: `Data loader stuck for ${routeName}: ${duration}ms`,
            detectedAt: Date.now(),
            preventionAction: async () => {
              console.log('🔧 [RootCauseDetector] Force resolving stuck loader');
              loaderStartTimes.delete(routeName);
              // Force page ready
              if ((window as any).useApplicationStore) {
                const appStore = (window as any).useApplicationStore();
                appStore.setState({ key: 'isPageReady', value: true });
              }
            }
          });
        }
      });
    };

    const loaderInterval = setInterval(checkStuckLoaders, 5000);
    this.intervals.push(loaderInterval);
  }

  /**
   * Monitor Vue reactivity issues
   */
  private monitorVueReactivity() {
    try {
      const applicationStore = useApplicationStore();

      // Watch for reactivity disconnects
      let lastAppReadyState = applicationStore.isAppReady;
      let lastPageReadyState = applicationStore.isPageReady;
      let stateCheckCount = 0;

      const checkReactivity = () => {
        const currentAppReady = applicationStore.isAppReady;
        const currentPageReady = applicationStore.isPageReady;
        
        // Check if state changed but UI didn't update
        if (currentAppReady !== lastAppReadyState || currentPageReady !== lastPageReadyState) {
          lastAppReadyState = currentAppReady;
          lastPageReadyState = currentPageReady;
          stateCheckCount = 0; // Reset counter
        } else {
          stateCheckCount++;
        }

        // If state hasn't changed for a while during loading, suspect reactivity issue
        if (stateCheckCount > 6 && !currentAppReady) { // 30 seconds of no state change
          this.reportIssue({
            id: 'vue-reactivity-stuck',
            category: 'reactivity',
            severity: 'critical',
            description: 'Vue reactivity appears stuck - state not updating',
            detectedAt: Date.now(),
            preventionAction: async () => {
              console.log('🔧 [RootCauseDetector] Force triggering Vue reactivity');
              // Force reactivity with nextTick
              const { nextTick } = await import('vue');
              await nextTick();
              applicationStore.setState({ key: 'isAppReady', value: true });
              await nextTick();
            }
          });
        }
      };

      const reactivityInterval = setInterval(checkReactivity, 5000);
      this.intervals.push(reactivityInterval);

    } catch (error) {
      console.warn('⚠️ [RootCauseDetector] Could not monitor Vue reactivity:', error);
    }
  }

  /**
   * Monitor router health
   */
  private monitorRouterHealth() {
    // Track navigation timeouts
    let lastRoute = '';
    let navigationStart = 0;
    let isInitialLoad = true;

    const checkRouter = () => {
      try {
        const currentRoute = (window as any).router?.currentRoute.value;
        if (!currentRoute) return;

        const currentPath = currentRoute.path;
        
        // Check if app is ready - don't monitor during initial startup
        const appElement = document.getElementById('app');
        const appLoaded = document.body.classList.contains('app-loaded');
        const hasAppContent = !!appElement?.children.length;
        
        if (isInitialLoad && appLoaded && hasAppContent) {
          // App just became ready, stop treating this as initial load
          isInitialLoad = false;
          lastRoute = currentPath; // Set current route as baseline
          console.log('🔍 [RootCauseDetector] App ready, starting router navigation monitoring');
          return; // Skip this check cycle
        }
        
        if (isInitialLoad) {
          // Still in initial load, don't monitor navigation speed
          return;
        }
        
        if (currentPath !== lastRoute) {
          // New navigation detected (after initial load)
          console.log(`🔍 [RootCauseDetector] Navigation detected: ${lastRoute} → ${currentPath}`);
          lastRoute = currentPath;
          navigationStart = Date.now();
        } else if (navigationStart > 0) {
          // Check if navigation is taking too long
          const duration = Date.now() - navigationStart;
          if (duration > 10000) { // >10 seconds for route resolution
            this.reportIssue({
              id: 'router-navigation-slow',
              category: 'router',
              severity: 'medium',
              description: `Router navigation slow: ${duration}ms for ${currentPath}`,
              detectedAt: Date.now()
            });
            navigationStart = 0; // Reset to avoid repeated reports
          }
        }
      } catch (error) {
        this.reportIssue({
          id: 'router-health-check-failed',
          category: 'router',
          severity: 'medium',
          description: `Router health check failed: ${error}`,
          detectedAt: Date.now()
        });
      }
    };

    const routerInterval = setInterval(checkRouter, 2000);
    this.intervals.push(routerInterval);
  }

  /**
   * Monitor performance issues
   */
  private monitorPerformance() {
    const checkPerformance = () => {
      // Check memory usage
      if ((performance as any).memory) {
        const memory = (performance as any).memory;
        const memoryUsage = memory.usedJSHeapSize / memory.jsHeapSizeLimit;
        
        if (memoryUsage > 0.8) { // >80% memory usage
          this.reportIssue({
            id: 'memory-usage-high',
            category: 'performance',
            severity: 'medium',
            description: `High memory usage: ${Math.round(memoryUsage * 100)}%`,
            detectedAt: Date.now(),
            preventionAction: async () => {
              console.log('🔧 [RootCauseDetector] Suggesting memory cleanup');
              // Force garbage collection if available
              if ((window as any).gc) {
                (window as any).gc();
              }
            }
          });
        }
      }

      // Check for long tasks (> 50ms)
      if ((window as any).PerformanceObserver) {
        try {
          const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
              if (entry.duration > 100) { // >100ms long task
                this.reportIssue({
                  id: 'long-task-detected',
                  category: 'performance',
                  severity: 'low',
                  description: `Long task detected: ${Math.round(entry.duration)}ms`,
                  detectedAt: Date.now()
                });
              }
            }
          });
          
          observer.observe({ entryTypes: ['longtask'] });
        } catch (error) {
          // PerformanceObserver not available
        }
      }
    };

    const performanceInterval = setInterval(checkPerformance, 15000); // Check every 15 seconds
    this.intervals.push(performanceInterval);
  }

  /**
   * Report a new issue
   */
  private reportIssue(issue: RootCauseIssue) {
    // Avoid duplicate issues
    const existingIssue = this.issues.value.find(i => i.id === issue.id);
    if (existingIssue) {
      // Update timestamp
      existingIssue.detectedAt = issue.detectedAt;
      return;
    }

    console.warn(`🚨 [RootCauseDetector] ${issue.severity.toUpperCase()}: ${issue.description}`);
    this.issues.value.push(issue);

    // Auto-execute prevention action for critical issues
    if (issue.severity === 'critical' && issue.preventionAction) {
      console.log('🔧 [RootCauseDetector] Auto-executing prevention action for critical issue');
      issue.preventionAction().catch(error => {
        console.error('❌ [RootCauseDetector] Prevention action failed:', error);
      });
    }

    // Clean up old issues
    this.cleanupOldIssues();
  }

  /**
   * Clear a specific issue
   */
  private clearIssue(issueId: string) {
    const index = this.issues.value.findIndex(i => i.id === issueId);
    if (index >= 0) {
      this.issues.value.splice(index, 1);
    }
  }

  /**
   * Clean up issues older than 5 minutes
   */
  private cleanupOldIssues() {
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
    this.issues.value = this.issues.value.filter(issue => issue.detectedAt > fiveMinutesAgo);
  }

  /**
   * Get issues by category
   */
  getIssuesByCategory(category: RootCauseIssue['category']): RootCauseIssue[] {
    return this.issues.value.filter(issue => issue.category === category);
  }

  /**
   * Get critical issues
   */
  getCriticalIssues(): RootCauseIssue[] {
    return this.issues.value.filter(issue => issue.severity === 'critical');
  }

  /**
   * Execute prevention action for an issue
   */
  async executePrevention(issueId: string): Promise<void> {
    const issue = this.issues.value.find(i => i.id === issueId);
    if (issue?.preventionAction) {
      try {
        await issue.preventionAction();
        console.log(`✅ [RootCauseDetector] Prevention executed for ${issueId}`);
        this.clearIssue(issueId);
      } catch (error) {
        console.error(`❌ [RootCauseDetector] Prevention failed for ${issueId}:`, error);
      }
    }
  }
}

// Export singleton instance
export const rootCauseDetector = RootCauseDetector.getInstance();